'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useRenderProgress } from '../hooks/use-render-progress';
import type { RemainingTime } from '../hooks/use-render-progress';
import { useLoadingContent } from '../hooks/use-loading-content';
import { useLoadingFacts } from '../hooks/use-loading-facts';
import { useDesignStage } from '../hooks/use-design-stage';
import { useTweenedNumber } from '../hooks/use-tweened-number';
import { DesignStageLine } from './DesignStageLine';
import { LoadingAnimation, PulsingDots } from '@/shared/ui';
import { useFitText } from '@/shared/hooks';
import { Card, CardContent } from '@/shared/ui/shadcn/card';
import { Progress } from '@/shared/ui/shadcn/progress';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Badge } from '@/shared/ui/shadcn/badge';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { cn } from '@/shared/lib/cn';
import type { Render } from '@/types/render';
import type { Control } from '@/types/concept';
import type { Job } from '@/features/design/job-queue';

export type RenderProgressMode = 'full' | 'inline' | 'bar' | 'minimal';

interface RenderProgressProps {
  render?: Render;
  control?: Control;
  queueRenderInfo?: Job; // Optional: full queue info for auto-detection
  mode?: RenderProgressMode;
  className?: string;
  projectId?: string;
  conceptId?: string;
  onComplete?: () => void;
  onError?: (error: string) => void;
  showContent?: boolean;
  showAnimation?: boolean;
  isUpscale?: boolean; // Flag to indicate this is an upscale operation (auto-detected from queueRenderInfo if available)
  /**
   * Render finished, image still prefetching (inline mode only). Shows the
   * bar pinned at 100% and suppresses the internal completion fade — the
   * parent (useSmoothRenderOverlay) owns the dissolve, timed to the image
   * being decoded. Mirrors Apple/Android `isPreloadingRender`.
   */
  isPreloading?: boolean;
}

type AnimationType = 'blueprint' | 'construction' | 'morph' | 'dots';

export function RenderProgress({
  render,
  control,
  queueRenderInfo,
  mode = 'full',
  className = '',
  projectId,
  conceptId,
  onComplete,
  onError,
  showContent = true,
  showAnimation = true,
  isUpscale = false,
  isPreloading = false,
}: RenderProgressProps) {
  const entityType = render ? 'render' : 'control';
  const entity = render || control;
  const tProgress = useTranslations('render.progress');
  const t = useTranslations('renderProgress');

  const formatRemaining = (time: RemainingTime | null): string | null => {
    if (!time) return null;
    return tProgress(
      time.type === 'minutes' ? 'minutesRemaining' : 'secondsRemaining',
      { count: time.count }
    );
  };

  // Auto-detect if this is an upscale operation from queueRenderInfo
  const isUpscaleOperation =
    queueRenderInfo?.jobType === 'upscale' || isUpscale;

  const {
    progress,
    error,
    isQueued,
    isProcessing,
    isCompleted,
    isFailed,
    isRetrying,
    isStalling,
    remainingTime,
  } = useRenderProgress({
    render,
    control,
    mode: entityType,
    onComplete,
    onError,
  });

  const { data: loadingFactsData } = useLoadingFacts(projectId);

  // Small rotating "design stage" narrated above the progress bar (Analyzing
  // materials, Verifying structural solutions, …) — the architect/engineer work
  // that sets us apart from tools that just generate a picture.
  const currentStage = useDesignStage(
    loadingFactsData?.stages,
    progress,
    isProcessing && !isUpscaleOperation
  );

  const { currentContent, userStats } = useLoadingContent({
    isActive: isProcessing,
    trackRender: entityType === 'render' && isProcessing,
    contextualFacts: loadingFactsData?.facts,
    seenIds: loadingFactsData?.seen_ids,
    // Absent flag (older API) → enabled.
    enabled: loadingFactsData?.loading_facts_enabled ?? true,
  });

  // The fact is held after it is cleared (rotation pause, or the render
  // finishing) so it can fade out instead of blinking off. During the preload
  // hold it stays lit and dissolves with the overlay itself.
  const [heldContent, setHeldContent] = useState(currentContent);
  useEffect(() => {
    if (currentContent) setHeldContent(currentContent);
  }, [currentContent]);
  const isFactLit = !!currentContent || isPreloading;

  // The fact renders at the size the design asks for and only shrinks if it
  // actually overflows its box (long fact, short landscape card, verbose
  // locale). Mirrors Apple `minimumScaleFactor` / Android `TextAutoSize`.
  const factRef = useFitText<HTMLParagraphElement>(heldContent?.text);

  // Bar and percentage share one clock: the value itself is eased (the CSS
  // transition is turned off for the bar below), so the pin to 100% at the end
  // of the render glides instead of snapping the digits.
  const tweenedProgress = useTweenedNumber(isPreloading ? 100 : progress, 700);
  const displayProgress = Math.round(tweenedProgress);

  // The stage caption is held for the same reason as the fact — and because
  // unmounting it would collapse a line of height under the progress bar and
  // shove the whole bottom block down mid-dissolve.
  const heldStageRef = useRef<string | null>(null);
  if (currentStage) heldStageRef.current = currentStage;
  const displayStage = currentStage ?? heldStageRef.current;

  // Same for the caption on the right of the bar: `remainingTime` goes null the
  // moment the job leaves the queue, so without holding it the text would blink
  // out a beat before the strip fades.
  const metaText = isUpscaleOperation
    ? tProgress('upscalingTo4K')
    : isStalling
      ? tProgress('takingLonger')
      : formatRemaining(remainingTime);
  const heldMetaRef = useRef<string | null>(null);
  if (metaText) heldMetaRef.current = metaText;
  const displayMeta = metaText ?? heldMetaRef.current;

  const [animationType, setAnimationType] =
    useState<AnimationType>('blueprint');
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [wasAlreadyCompleted] = useState(isCompleted);

  // Rotate animation type
  useEffect(() => {
    if (!isProcessing || mode === 'minimal' || mode === 'bar') return;

    // For upscale, use only 'morph' animation (image enhancement)
    if (isUpscaleOperation) {
      setAnimationType('morph');
      return;
    }

    const animations: AnimationType[] = ['blueprint', 'construction', 'morph'];
    let currentIndex = 0;

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % animations.length;
      const nextAnimation = animations[currentIndex];
      if (nextAnimation) {
        setAnimationType(nextAnimation);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isProcessing, mode, isUpscaleOperation]);

  // Handle completion with fade out for inline mode. While the parent holds
  // the overlay for the image prefetch (isPreloading), the dissolve belongs
  // to it — the internal fade must not race a second, faster opacity drop.
  useEffect(() => {
    if (isCompleted && !isPreloading && mode === 'inline' && !isFadingOut) {
      setIsFadingOut(true);
      setTimeout(() => {
        onComplete?.();
      }, 500);
    }
  }, [isCompleted, isPreloading, mode, isFadingOut, onComplete]);

  // Auto-redirect for already completed renders
  useEffect(() => {
    if (wasAlreadyCompleted && isCompleted && mode === 'full') {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [wasAlreadyCompleted, isCompleted, mode, onComplete]);

  // Render based on mode
  if (mode === 'bar') {
    if (!isProcessing && !isQueued) return null;

    return (
      <div className={cn('w-full', className)}>
        {isQueued ? (
          <div className="w-full h-2 flex items-center justify-center">
            <PulsingDots size="sm" />
          </div>
        ) : (
          <Progress value={progress} className="h-2" isStalling={isStalling} />
        )}
      </div>
    );
  }

  if (mode === 'minimal') {
    if (!isProcessing && !isQueued) return null;

    return (
      <div className={cn('flex items-center gap-2', className)}>
        {isQueued ? (
          <>
            <PulsingDots size="sm" />
            <span className="text-list-item-subtitle text-muted-foreground">{t('waiting')}</span>
          </>
        ) : (
          <>
            <Progress
              value={progress}
              className="h-1.5 w-24"
              isStalling={isStalling}
            />
            <span className="text-progress-value text-muted-foreground">{progress}%</span>
          </>
        )}
      </div>
    );
  }

  if (mode === 'inline') {
    const shouldShow =
      isQueued || isProcessing || isPreloading || (isCompleted && isFadingOut);
    // Hide progress on failure - toast will handle error display
    if (!shouldShow || isFailed) return null;

    // Preload hold: the render is done, the image is decoding — pin the bar
    // at 100% and drop queue/retry/remaining-time affordances (the retained
    // job may have already left the queue, which reads as PENDING here).
    const showAsQueued = isQueued && !isPreloading;

    // The ambient glow (AIGlowOverlay) is a soft inward feather, not a thick
    // travelling frame, so the overlay no longer has to reserve 15% of the card
    // width for it. Plain padding, capped proportionally so it still breathes
    // on small cards.
    return (
      <div
        className="absolute inset-0 z-10 [container-type:size]"
      >
        <div
          className={cn(
            // The fact floats in the flexible top area; the stage line stays
            // glued just above the bar (it's a progress caption), both at the
            // bottom.
            'absolute inset-0 bg-black/55 backdrop-blur-md flex flex-col transition-opacity duration-500 px-[min(6cqw,32px)] py-[min(6cqw,24px)]',
            isFadingOut ? 'opacity-0' : 'opacity-100',
            className
          )}
        >
          {/* Fact/tip text — floats in the flexible upper area. No animated icon
              above it; the reserved icon space squeezed the fact text into
              truncation, so it was dropped. Apple/Android mirror. */}
          <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden w-full">
            {showContent && heldContent && (
              <p
                // No `key` and no entrance animation: one persistent element
                // that fades between facts. A keyed remount plus
                // `animate-fade-in` (fill-forwards) pinned opacity at 1 and made
                // every disappearance a hard cut.
                ref={factRef}
                className={cn(
                  'text-progress-fact text-center text-white text-balance max-w-md',
                  'transition-opacity duration-500 ease-out',
                  isFactLit ? 'opacity-100' : 'opacity-0'
                )}
              >
                {heldContent.text}
              </p>
            )}
          </div>

          {/* Progress Bar — bottom. */}
          <div className="w-full">
            {isRetrying && !isPreloading ? (
              <div className="text-center">
                <p className="text-list-item-value text-white animate-pulse">
                  {tProgress('rethinking')}
                </p>
              </div>
            ) : showAsQueued ? (
              <div className="w-full h-2 flex items-center justify-center">
                <PulsingDots size="sm" className="text-white/80" />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Design stage — glued directly above the bar, left-aligned.
                    Replaced as a belt (rolls up from below) rather than
                    crossfaded, so the caption reads as live, advancing work. */}
                {displayStage && (
                  <div
                    className={cn(
                      'transition-opacity duration-500 ease-out',
                      isPreloading ? 'opacity-0' : 'opacity-100'
                    )}
                  >
                    <DesignStageLine
                      text={displayStage}
                      className="text-progress-meta text-start text-white/70"
                    />
                  </div>
                )}
                <Progress
                  value={displayProgress}
                  className="h-2 bg-white/20"
                  // The value is already eased frame by frame (useTweenedNumber);
                  // a CSS transition on top of it would drag a second, slower
                  // clock behind the digits.
                  indicatorClassName="transition-none"
                  isStalling={isStalling && !isPreloading}
                />
                <div className="flex items-center justify-between">
                  <span className="text-progress-value text-white">{displayProgress}%</span>
                  {/* Fades rather than vanishing when the render lands: the
                      whole strip has to dissolve as one. */}
                  <span
                    className={cn(
                      'transition-opacity duration-500 ease-out',
                      isPreloading ? 'opacity-0' : 'opacity-100'
                    )}
                  >
                    {displayMeta ? (
                      <span className="text-progress-meta text-white/80 text-balance">
                        {displayMeta}
                      </span>
                    ) : null}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full mode (default)
  if (isFailed) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertDescription className="text-center">
          <div className="font-medium mb-2">
            {entityType === 'render'
              ? t('failed.render')
              : t('failed.control')}
          </div>
          <div className="text-form-help">
            {error || t('failed.unexpected')}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (isCompleted) {
    if (wasAlreadyCompleted) {
      return (
        <NoticeBanner
          variant="warning"
          title={t('alreadyExists.title')}
          message={t('alreadyExists.returning')}
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onComplete?.()}
            >
              {t('alreadyExists.returnNow')}
            </Button>
          }
          className={className}
        />
      );
    }

    const completedTitle = isUpscaleOperation
      ? t('completed.upscale')
      : entityType === 'render'
        ? t('completed.render')
        : t('completed.control');
    const completedMessage = isUpscaleOperation
      ? t('completed.upscaleDescription')
      : userStats.renderCount > 0 && entityType === 'render'
        ? t('completed.designsCount', { count: userStats.renderCount })
        : undefined;

    return (
      <NoticeBanner
        variant="success"
        title={completedTitle}
        message={completedMessage}
        className={className}
      />
    );
  }

  return (
    <Card
      className={cn(
        'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20',
        className
      )}
    >
      <CardContent className="p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Animation */}
          {showAnimation && (
            <div className="flex justify-center mb-4 md:mb-6 scale-50 md:scale-100">
              <LoadingAnimation
                type={isQueued ? 'blueprint' : animationType}
                size="lg"
              />
            </div>
          )}

          {/* Content Display */}
          {showContent && !isUpscaleOperation && currentContent && (
            <div className="mb-8 text-center animate-fade-in">
              <div className="p-6">
                <p className="text-card-title">{currentContent.text}</p>
              </div>
            </div>
          )}

          {/* Upscale specific content */}
          {isUpscaleOperation && (
            <div className="mb-8 text-center animate-fade-in">
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div
                    className="text-section-eyebrow text-white border border-white/50 rounded-full px-3 py-1"
                    style={{ borderWidth: '0.5px' }}
                  >
                    {t('upscale.badge')}
                  </div>
                </div>
                <p className="text-card-title mb-2">
                  {t('upscale.heading')}
                </p>
                <p className="text-section-subtitle text-muted-foreground">
                  {t('upscale.subheading')}
                </p>
              </div>
            </div>
          )}

          {/* Progress Information */}
          <div className="space-y-4">
            {/* Progress Bar or Queue Spinner */}
            {isQueued ? (
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-list-item-value">
                    {t(
                      isUpscaleOperation
                        ? 'queuedLabel.upscale'
                        : entityType === 'render'
                          ? 'queuedLabel.render'
                          : 'queuedLabel.control',
                      { id: entity?.id ?? '' }
                    )}
                  </span>
                  <Badge
                    variant="outline"
                    className="border-yellow-500 text-yellow-600"
                  >
                    {t('waiting')}
                  </Badge>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden flex items-center justify-center">
                  <LoadingAnimation type="dots" size="sm" />
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-list-item-value">
                    {t(
                      isUpscaleOperation
                        ? 'processingLabel.upscale'
                        : entityType === 'render'
                          ? 'processingLabel.render'
                          : 'processingLabel.control',
                      { id: entity?.id ?? '' }
                    )}
                  </span>
                  <span className="text-list-item-value text-primary">
                    {progress}%
                  </span>
                </div>
                <Progress
                  value={progress}
                  className="h-3"
                  isStalling={isStalling}
                />
              </div>
            )}

            {/* Status Message */}
            <div className="text-center">
              <div className="space-y-2">
                {(isQueued || isUpscaleOperation) && (
                  <p className="text-list-item-value">
                    {isQueued ? t('status.waiting') : t('status.upscaling')}
                  </p>
                )}
                <p className="text-card-meta text-muted-foreground">
                  {isQueued &&
                    (isUpscaleOperation
                      ? t('status.upscaleQueued')
                      : t('status.renderQueued'))}
                  {isProcessing &&
                    isUpscaleOperation &&
                    progress < 50 &&
                    t('status.upscaleEarly')}
                  {isProcessing &&
                    isUpscaleOperation &&
                    progress >= 50 &&
                    t('status.upscaleLate')}
                  {isProcessing &&
                    !isUpscaleOperation &&
                    progress < 50 &&
                    t('status.renderEarly')}
                  {isProcessing &&
                    !isUpscaleOperation &&
                    progress >= 50 &&
                    t('status.renderLate')}
                </p>
              </div>
            </div>

            {/* Remaining Time / Stalling */}
            {isProcessing && (remainingTime || isStalling) && (
              <div className="text-center text-progress-meta text-muted-foreground">
                <p>
                  {isStalling
                    ? tProgress('takingLonger')
                    : formatRemaining(remainingTime)}
                </p>
              </div>
            )}

            {/* Back link */}
            {entityType === 'render' && projectId && conceptId && (
              <div className="mt-6 text-center">
                <p className="text-section-subtitle text-muted-foreground">
                  {t('backHint')}
                </p>
                <Link
                  href={`/project/${projectId}/concept/${conceptId}`}
                  className="inline-block mt-4 text-primary hover:text-primary/80"
                >
                  {t('backToConcept')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
